import { render } from "preact";
import { useEffect } from "preact/hooks";
import { startAnalytics } from "~/web/src/analytics";
import { account, authState, signIn, startAuth } from "~/web/src/auth";
import { Wordmark } from "~/web/src/brand";
import { keepFirstTouch } from "~/web/src/first-touch";
import { startGoogleAnalytics } from "~/web/src/google-analytics";
import { ApiKeysPage } from "~/web/src/pages/api-keys";
import { BillingPage } from "~/web/src/pages/billing";
import { ProjectsPage } from "~/web/src/pages/projects";
import { route } from "~/web/src/router";
import { VerifyFirst } from "~/web/src/verify-first";
import "~/web/src/styles.css";
import { Toaster } from "sonner";

// The SPA (spec: docs/project/web-workspace-spec.md): one projects page that
// is the list at /projects and the run at /projects/:id, plus billing and the
// API keys; preact + signals,
// bundled by esbuild into web/dist/assets. The marketing site at / is static
// HTML (server/site.ts), not this bundle. Everything here sits behind the
// auth gate: pages mount only once /api/me answers for the session cookie, so
// the workspace channel never opens signed out.

function Shell({ note, children }: { note?: string; children?: preact.ComponentChildren }) {
  return (
    <div class="app">
      <header class="topbar">
        <span class="logo">
          <Wordmark tone="white" />
        </span>
      </header>
      <main class="page gate">
        {note && <span class="dim">{note}</span>}
        {children}
      </main>
    </div>
  );
}

// Signed out: the login page, back to this URL once the mailed link is clicked.
function Gate({ children }: { children: preact.ComponentChildren }) {
  const state = authState.value;
  useEffect(() => {
    void startAuth();
  }, []);
  useEffect(() => {
    if (state === "signed_out") signIn(location.href);
  }, [state]);
  switch (state) {
    case "ready":
      return <>{children}</>;
    case "signed_out":
      return <Shell note="redirecting to sign-in…" />;
    default:
      return <Shell note="loading…" />;
  }
}

function App() {
  const r = route.value;
  // Money and keys wait for the mailed link (own-auth-spec decision 6); decided here, not in the
  // pages, so their hooks run the same on every render.
  const unverified = account.value?.verifiedAt === null;
  // TODO(200sc): do we like using the same entrypoint for two routes?
  // would it be more flexible to export multiple entrypoints?
  return (
    <Gate>
      {r.page === "workspace" ? (
        <ProjectsPage id={r.id} />
      ) : r.page === "projects" ? (
        <ProjectsPage />
      ) : r.page === "billing" ? (
        unverified ? (
          <VerifyFirst name="Billing" />
        ) : (
          <BillingPage />
        )
      ) : unverified ? (
        <VerifyFirst name="API keys" />
      ) : (
        <ApiKeysPage />
      )}
      <Toaster />
    </Gate>
  );
}

// A landing here is the tab's first touch, recorded now; the gate waits for the cookie before
// the pages mount.
void keepFirstTouch();
startAnalytics();
startGoogleAnalytics();
const root = document.getElementById("app");
if (root) render(<App />, root);
